import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ElementRef, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
import { TranslateService } from "@ngx-translate/core";
import { Subject, Subscription } from "rxjs";
import { finalize, takeUntil } from "rxjs/operators";
import { Player } from "src/app/core/interfaces/api/player.interface";
import { PlayerService } from "src/app/core/services/api/player.service";
import { BootstrapModalService } from "src/app/core/services/ui/bootstrap-modal.service";
import { ModalWithAction } from "src/app/core/interfaces/ui/bootstrap-modal.interface";
import { selectAuth } from "src/app/core/states/auth/auth.selectors";
import { User } from "src/app/core/interfaces/api/user.interface";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/core/states/app.state";
import { FilterService } from "src/app/core/services/ui/filter.service";
import { title } from "process";
import { NoteService } from "src/app/core/services/api/note.service";
import { GlobalService } from "src/app/core/services/ui/global.service";
import { PLAYER_NOTE_MAX_LENGTHS } from "src/app/core/helpers/global/player.constant";

@Component({
  selector: "app-player-notes",
  templateUrl: "./player-notes.component.html",
})
export class PlayerNoteFormComponent implements OnInit, OnDestroy {
  @Input() player: Player | null = null;
  @Input() inline = false;
  @Output() saved = new EventEmitter<void>();

  @ViewChild("noteTitleInput") private noteTitleInput?: ElementRef<HTMLInputElement>;
  @ViewChild("noteContentInput") private noteContentInput?: ElementRef<HTMLTextAreaElement>;

  public isSubmitting = false;
  public playerId: string | undefined;
  public readonly maxLengths = PLAYER_NOTE_MAX_LENGTHS;

  public playerNotesForm: FormGroup | undefined = undefined;
  private unsubscribe$: Subject<boolean> = new Subject<boolean>();

  public userProfile!: User;
  private profile$ = this._globalService.profile

  private _subscriptions: Subscription[] = [];

  constructor(
    private _formBuilder: FormBuilder,
    private _playerService: PlayerService,
    private _bsModalService: BootstrapModalService<ModalWithAction<any>>,
    private _toastr: ToastrService,
    private _translateService: TranslateService,
    private _store: Store<AppState>,
    private _filterService: FilterService<object>,
    private _noteService: NoteService,
    private _globalService: GlobalService
  ) {}

  ngOnInit(): void {
    this.getProfile();
    this.playerNotesForm = this.initializeForm();
    if (!this.inline) {
      this.subscribeToModalData();
    }
  }

  private getProfile(): void {
    this._subscriptions.push(
      this.profile$.subscribe((value) => {
        this.userProfile = value;
      })
    );
  }

  private initializeForm(): FormGroup {
    return this._formBuilder.group({
      content: ["", [Validators.required, Validators.maxLength(PLAYER_NOTE_MAX_LENGTHS.CONTENT)]],
      title: ["", [Validators.required, Validators.maxLength(PLAYER_NOTE_MAX_LENGTHS.TITLE)]],
    });
  }

  private subscribeToModalData(): void {
    this._bsModalService
      .getDataIssued()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: ({ selectedRow }) => {
          this.playerId = selectedRow?._id;
        },
        error: () => this.closeModal(),
      });
  }

  public onSubmit(): void {
    const playerId = this.inline ? this.player?._id : this.playerId;
    if (this.inline && this.playerNotesForm?.invalid) {
      this.flagMissingFields();
      return;
    }
    if (this.playerNotesForm?.invalid || !playerId || this.isSubmitting) {
      return;
    }

    const formValues = this.playerNotesForm?.value;

    const request = {
      assigningId: playerId,
      assigningType: "Player",
      assignedId: this.userProfile._id,
      assignedType: "User",
      title: formValues.title,
      content: formValues.content,
    };

    this.isSubmitting = true;
    this._noteService
      .createPlayerNote(request)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => {
          if (this.inline) {
            this.playerNotesForm?.reset({ title: "", content: "" });
            this.saved.emit();
            return;
          }
          this.afterSubmitForm();
          this.closeModal();
        },
      });
  }

  private flagMissingFields(): void {
    this.playerNotesForm?.markAllAsTouched();
    const target = this.playerNotesForm?.get("title")?.invalid
      ? this.noteTitleInput
      : this.noteContentInput;
    target?.nativeElement.focus();
  }

  public closeModal(): void {
    if (this.inline) return;
    this._bsModalService.closeModal();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }

  private afterSubmitForm(): void {
    this._filterService.updateFilterData({});
    this.closeModal();
  }
}
